/**
 * DynamicKycForm — renders the KYC submission form based on the admin's
 * configured schema for the current user's role bucket.
 *
 * Loads schema from `GET /api/kyc/form-schema` (per-user), then submits to
 * `POST /api/kyc/submit-dynamic` (multipart form data). All fields are
 * grouped by `field.group` for clean visual sections.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Spinner } from '@phosphor-icons/react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DynamicKycForm = ({ onSubmitted }) => {
  const [loading, setLoading] = useState(true);
  const [schema, setSchema] = useState(null);
  const [values, setValues] = useState({});
  const [files, setFiles] = useState({});
  const [subTypes, setSubTypes] = useState({}); // for upload fields with options
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { loadSchema(); }, []); // eslint-disable-line

  const loadSchema = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/kyc/form-schema`,
        { withCredentials: true });
      setSchema(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load KYC form');
    } finally { setLoading(false); }
  };

  const setVal = (key, v) => setValues(prev => ({ ...prev, [key]: v }));
  const setFile = (key, f) => setFiles(prev => ({ ...prev, [key]: f }));

  const submit = async (e) => {
    e.preventDefault();
    if (!schema) return;
    // Client-side required check (server validates again — defence in depth).
    for (const f of schema.fields) {
      if (!f.required) continue;
      if (f.type === 'upload' && !files[f.key]) {
        toast.error(`Please upload "${f.label}"`);
        return;
      }
      if (f.type !== 'upload' && !(values[f.key] ?? '').toString().trim()) {
        toast.error(`Please fill "${f.label}"`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      schema.fields.forEach(f => {
        if (f.type === 'upload') {
          if (files[f.key]) fd.append(f.key, files[f.key]);
          if (subTypes[f.key]) fd.append(`${f.key}_type`, subTypes[f.key]);
        } else if (values[f.key] != null && values[f.key] !== '') {
          fd.append(f.key, String(values[f.key]));
        }
      });
      const { data } = await axios.post(
        `${BACKEND_URL}/api/kyc/submit-dynamic`, fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      toast.success(`KYC submitted (${data.saved_fields.length + data.uploaded_fields.length} fields). Admin will review shortly.`);
      onSubmitted?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submit failed');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <p className="text-sm text-text-secondary flex items-center gap-2">
        <Spinner size={14} className="animate-spin" /> Loading form…
      </p>
    );
  }
  if (!schema) return null;

  // Group fields by `group` so we can show section headings.
  const groups = {};
  schema.fields.forEach(f => {
    const g = f.group || 'Details';
    (groups[g] ||= []).push(f);
  });

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="dynamic-kyc-form">
      {Object.entries(groups).map(([group, fields]) => (
        <div key={group} className="space-y-3">
          <h4 className="text-[10px] font-bold uppercase tracking-wider text-brand-primary border-b border-[#E8E1D5] pb-1">
            {group}
          </h4>
          {fields.map(f => {
            const opts = Array.isArray(f.options) ? f.options : [];
            return (
              <div key={f.key} className="space-y-1" data-testid={`kyc-fld-${f.key}`}>
                <Label className="text-xs font-bold">
                  {f.label}
                  {f.required && <span className="text-red-600 ml-0.5">*</span>}
                </Label>
                {f.type === 'text' || f.type === 'pan' || f.type === 'phone' ? (
                  <Input value={values[f.key] || ''}
                    onChange={(e) => setVal(f.key, e.target.value)}
                    placeholder={f.placeholder || ''}
                    className="rounded-none border-[#E8E1D5] h-9" />
                ) : f.type === 'textarea' ? (
                  <Textarea value={values[f.key] || ''}
                    onChange={(e) => setVal(f.key, e.target.value)}
                    placeholder={f.placeholder || ''}
                    className="rounded-none border-[#E8E1D5]" rows={3} />
                ) : f.type === 'number' ? (
                  <Input type="number" value={values[f.key] || ''}
                    onChange={(e) => setVal(f.key, e.target.value)}
                    placeholder={f.placeholder || ''}
                    className="rounded-none border-[#E8E1D5] h-9 font-mono" />
                ) : f.type === 'dropdown' ? (
                  <Select value={values[f.key] || ''} onValueChange={(v) => setVal(f.key, v)}>
                    <SelectTrigger className="rounded-none border-[#E8E1D5] h-9">
                      <SelectValue placeholder={f.placeholder || 'Pick one'} />
                    </SelectTrigger>
                    <SelectContent>
                      {opts.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : f.type === 'location' ? (
                  <Input value={values[f.key] || ''}
                    onChange={(e) => setVal(f.key, e.target.value)}
                    placeholder={f.placeholder || 'Chennai / Coimbatore / Madurai…'}
                    className="rounded-none border-[#E8E1D5] h-9" />
                ) : f.type === 'note' ? (
                  <p className="text-xs text-text-secondary italic">{f.placeholder || f.help}</p>
                ) : f.type === 'upload' ? (
                  <div className="space-y-2">
                    {opts.length > 0 && (
                      <Select value={subTypes[f.key] || ''}
                        onValueChange={(v) => setSubTypes(prev => ({ ...prev, [f.key]: v }))}>
                        <SelectTrigger className="rounded-none border-[#E8E1D5] h-9">
                          <SelectValue placeholder="Document type" />
                        </SelectTrigger>
                        <SelectContent>
                          {opts.map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <Input type="file" accept={f.accept || 'image/*,application/pdf'}
                      onChange={(e) => setFile(f.key, e.target.files?.[0] || null)}
                      className="rounded-none border-[#E8E1D5] h-9 text-xs" />
                    {files[f.key] && (
                      <p className="text-[10px] text-text-secondary">
                        Selected: <strong>{files[f.key].name}</strong>
                      </p>
                    )}
                  </div>
                ) : null}
                {f.help && (
                  <p className="text-[10px] text-text-secondary italic">{f.help}</p>
                )}
              </div>
            );
          })}
        </div>
      ))}

      <Button type="submit" disabled={submitting}
        className="bg-brand-primary text-sand-50 rounded-none flex items-center gap-2 disabled:opacity-50"
        data-testid="dynamic-kyc-submit-btn">
        <Upload size={14} /> {submitting ? 'Submitting…' : 'Submit for Review'}
      </Button>
    </form>
  );
};

export default DynamicKycForm;
